import { noop } from 'lodash/fp';
import { memo, ReactElement, ReactNode } from 'react';
import { MEDIA_QUERIES, SPACINGS } from 'shared/styles/variables';
import styled, { css, ThemeProvider } from 'styled-components';

import { isFullscreenPage } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import { ScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { PageUnavailable } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/PageUnavailable';
import StalledAnimationContainer from 'dashboard/apps/InquiryTemplateEditor/StalledAnimationContainer';
import ThemedFonts from 'dashboard/components/ThemedFonts';
import Tooltip from 'dashboard/components/Tooltip';
import { Paragraph } from 'design-system/components/Text';
import { isHovered, isNotDisabled, isNotFocused } from 'design-system/lib/pseudoClass';
import { Elevation } from 'design-system/variables/effect';
import { Container as FooterContainer } from 'inquiry/components/Footer/Footer';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { ApiModel } from 'lib/interfaces/api';
import { isCombinedStep, isUiStep, StepPreview, UiStepType } from 'lib/interfaces/StepsConfig';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import Navbar from 'verify/components/Navbar/Navbar';
import { serializeTheme } from 'verify/lib/theme';
import { ThemeAttributes } from 'verify/models/theme';

export const PREVIEW_WIDTH = 400;

const ScreenContainer = styled.div`
  pointer-events: auto;
  cursor: pointer;
`;

const Slide = styled.div.attrs<{ screenDimensions?: ScreenDimensions }>((props) => ({
  style: {
    width: `${props?.screenDimensions?.width ?? PREVIEW_WIDTH}px`,
    height: `${props?.screenDimensions?.height || FRAME_HEIGHT}px`,
  },
}))<{
  selected?: boolean;
  screenDimensions?: ScreenDimensions;
}>`
  box-shadow: ${Elevation.High};
  border-radius: ${({ theme }) => theme.borderRadiusModal};
  pointer-events: none;
  overflow-y: auto;

  ${(props) =>
    props.selected
      ? css`
          outline: 2px solid ${({ theme }) => theme.color.primary.brand600};
        `
      : css`
          &${isHovered}${isNotDisabled}${isNotFocused} {
            outline: 2px solid ${({ theme }) => theme.color.primary.brand200};
            cursor: pointer;
          }
        `}
`;

// wrapper is for position: absolute elements to be properly positioned inside the scroll view
const PageContentWrapper = styled.div<{ isFullscreenPage: boolean }>`
  min-height: ${FRAME_HEIGHT}px;
  height: 100%;
  position: relative;
  ${({ isFullscreenPage }) =>
    !isFullscreenPage &&
    css`
      // prettier-ignore
      padding: ${SPACINGS.navHeight} ${SPACINGS.widgetPadding} ${SPACINGS.widgetPadding} ${SPACINGS.widgetPadding};
    `};

  // override Footer component styling
  // don't set footer position to fixed based on editor viewport size
  ${FooterContainer} {
    @media ${MEDIA_QUERIES.isMobile} {
      position: absolute;
    }
  }
`;

const NavContainer = styled.div`
  position: relative;
  margin: -${SPACINGS.navHeight} -${SPACINGS.widgetPadding} 0px;
  z-index: 1;

  nav {
    position: relative;
  }
`;

export interface InquiryTemplateScreenPreviewProps {
  page: ReactElement | undefined;
  /**
   * Is undefined for expanded combined steps, etc.
   */
  step: StepPreview | undefined;
  theme?: ThemeAttributes;
  selected?: boolean;
  skipReason?: string;
  screenDimensions?: ScreenDimensions;
  nodeHighlighter?: ReactNode;
  tooltipText?: string;
  onPreviewDoubleClick?: (stepName: string, pageKey?: string) => void;
}

const InquiryTemplateScreenPreview = ({
  page,
  step,
  theme,
  selected,
  skipReason,
  screenDimensions,
  nodeHighlighter,
  tooltipText,
  onPreviewDoubleClick,
}: InquiryTemplateScreenPreviewProps) => {
  const serializedTheme = serializeTheme({
    id: 'mockTheme',
    type: ApiModel.Theme,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    attributes: theme,
  });
  const stepBackgroundColor =
    isUiStep(step) || isCombinedStep(step)
      ? colorCss(step?.styles?.['background-color'].base.base)
      : undefined;
  const backgroundColor = stepBackgroundColor ?? serializedTheme.colorModalBackground;

  const headerComponent = isUiStep(step) ? (
    <Navbar
      logoUrl={serializedTheme.logoUrl}
      logoDisplay={serializedTheme.navbarLogoDisplay}
      colorIconHeader={colorCss(step?.styles?.['text-color']?.['header-button']?.base)}
      styles={{
        backgroundColor: colorCss(step?.styles?.['background-color']?.header?.base),
        borderColor: colorCss(step?.styles?.['border-color']?.header?.base),
        borderWidth: sizeCss(step?.styles?.['border-width']?.header?.base?.bottom),
      }}
      showBackButton={step.config['back-step-enabled']}
      showCloseButton={step.config['cancel-button-enabled'] && !step.config.terminal}
      onBack={noop}
      onClose={noop}
    />
  ) : null;

  const previewContent = (
    <ThemeProvider theme={serializedTheme}>
      <ThemedFonts>
        <Slide selected={selected} style={{ backgroundColor }} screenDimensions={screenDimensions}>
          {nodeHighlighter}
          <PageContentWrapper isFullscreenPage={isFullscreenPage(page)}>
            {headerComponent && <NavContainer>{headerComponent}</NavContainer>}
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <StalledAnimationContainer playAnimation={selected}>{page}</StalledAnimationContainer>
          </PageContentWrapper>
        </Slide>
      </ThemedFonts>
    </ThemeProvider>
  );

  const ScreenContainerDOM = (
    <ScreenContainer
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onPreviewDoubleClick) {
          // Page key only needed for combined steps, as a single step maps to multiple preview pages
          const pageKey = step?.type === UiStepType.Ui ? null : page?.key?.toString();
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onPreviewDoubleClick(step.name, pageKey);
        }
      }}
    >
      {tooltipText ? (
        <Tooltip trigger='hover' tooltip={<Paragraph>{tooltipText}</Paragraph>} placement='top'>
          {previewContent}
        </Tooltip>
      ) : (
        previewContent
      )}
    </ScreenContainer>
  );

  return skipReason != null ? (
    <PageUnavailable popoverText={skipReason}>{ScreenContainerDOM}</PageUnavailable>
  ) : (
    ScreenContainerDOM
  );
};

export default memo(InquiryTemplateScreenPreview);
